import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serviceRequestService } from '../../services/service-request.service';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Loading } from '../../components/ui/Loading';

const SERVICE_TYPES = {
  HOSPITAL_APPOINTMENT: { label: 'Hospital Appointment', icon: '🏥', urgency: 'NORMAL' },
  DIAGNOSTIC_TEST: { label: 'Diagnostic Test', icon: '🔬', urgency: 'NORMAL' },
  INSURANCE_SUPPORT: { label: 'Insurance Support', icon: '📋', urgency: 'NORMAL' },
  AMBULANCE: { label: 'Ambulance Request', icon: '🚑', urgency: 'EMERGENCY' },
  HOME_CARE: { label: 'Home Care', icon: '🏠', urgency: 'NORMAL' },
  COORDINATOR_CONTACT: { label: 'Contact Service Coordinator', icon: '📞', urgency: 'NORMAL' }
};

export const CreateRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type') || '';

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: typeParam,
    details: '',
    date: '',
    time: '',
    priority: 'NORMAL',
    location: ''
  });

  // Effect to set initial type and default priority
  useEffect(() => {
    if (typeParam && SERVICE_TYPES[typeParam as keyof typeof SERVICE_TYPES]) {
      setFormData(prev => ({
        ...prev,
        type: typeParam,
        priority: SERVICE_TYPES[typeParam as keyof typeof SERVICE_TYPES].urgency
      }));
    }
  }, [typeParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.type) {
      toast.error('Please select a service type');
      return;
    }

    try {
      setLoading(true);

      // Construct details string with all info
      let fullDetails = formData.details;
      if (formData.date) fullDetails += `\nPreferred Date: ${formData.date}`;
      if (formData.time) fullDetails += `\nPreferred Time: ${formData.time}`;
      if (formData.location) fullDetails += `\nLocation: ${formData.location}`;

      await serviceRequestService.createRequest({
        type: formData.type,
        details: fullDetails,
        priority: formData.priority
      });

      toast.success('Request submitted successfully!');
      navigate('/member/requests');
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (formData.type && SERVICE_TYPES[formData.type as keyof typeof SERVICE_TYPES]) {
      const typeInfo = SERVICE_TYPES[formData.type as keyof typeof SERVICE_TYPES];
      return `${typeInfo.icon} Request ${typeInfo.label}`;
    }
    return 'Create New Request';
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
        <p className="text-gray-600">Fill in the details below and we will process your request shortly.</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Service Type Selection (if not pre-selected) */}
          {!typeParam && (
            <Select
              label="Service Type"
              value={formData.type}
              onChange={(e) => {
                const newType = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  type: newType,
                  priority: SERVICE_TYPES[newType as keyof typeof SERVICE_TYPES]?.urgency || 'NORMAL'
                }));
              }}
              options={[
                { value: '', label: 'Select Service Type' },
                ...Object.entries(SERVICE_TYPES).map(([key, value]) => ({
                  value: key,
                  label: value.label
                }))
              ]}
              required
            />
          )}

          {/* Location Field (Conditional) */}
          {(formData.type === 'AMBULANCE' || formData.type === 'HOME_CARE') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Location / Address
              </label>
              <textarea
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                rows={2}
                placeholder="Enter complete address..."
                required
              />
            </div>
          )}

          {/* Date & Time Fields (Conditional) */}
          {(formData.type === 'HOSPITAL_APPOINTMENT' || formData.type === 'DIAGNOSTIC_TEST') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Preferred Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
              <Input
                label="Preferred Time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              />
            </div>
          )}

          {/* Description / Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.type === 'INSURANCE_SUPPORT' ? 'Query Details' : 'Additional Details / Symptoms'}
            </label>
            <textarea
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder={formData.type === 'INSURANCE_SUPPORT' ? 'Describe your insurance query...' : 'Please describe your requirement or symptoms...'}
              required
            />
          </div>

          {/* Priority Selection */}
          <Select
            label="Urgency Level"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            options={[
              { value: 'NORMAL', label: 'Normal Priority' },
              { value: 'EMERGENCY', label: 'Emergency / Urgent' }
            ]}
          />

          {/* Warning for Emergency */}
          {formData.priority === 'EMERGENCY' && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-500 text-xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    For life-threatening medical emergencies, please call <strong>108</strong> or our emergency hotline immediately instead of using this form.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <Button variant="secondary" type="button" onClick={() => navigate('/member')}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loading size="sm" /> : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
